import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import coachRoutes from '@/router/coach'
import studentRoutes from '@/router/student';
import companyRoutes from "@/router/company";
import { Storage } from "@/utils/Storage";
import { waitForAuth } from "@/utils/Firebase";
import { skipsBilling } from "@/utils/Helpers";
import { isIosApp, paymentsEnabled } from "@/utils/Platform";

Vue.use(VueRouter)

const PAYMENT_ROUTES = [
    'coach.select-plan', 'coach.credit-card', 'coach.payment.edit',
    'company.select-plan', 'company.credit-card', 'company.payment.edit',
]

const routes = [
    ...coachRoutes,
    ...studentRoutes,
    ...companyRoutes,
    {
        path: '/',
        name: 'splash',
        component: () => import('@/views/entrance/Splash'),
        meta: {
            public: true,
        }
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/entrance/Login'),
        meta: {
            public: true,
        }
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('@/views/entrance/Register'),
        meta: {
            public: true,
        }
    },
    {
        path: '/confirm-mail',
        name: 'confirm-mail',
        component: () => import('@/views/entrance/ConfirmMail'),
        meta: {}
    },
    {
        path: '/auth/action',
        name: 'auth-action',
        component: () => import('@/views/entrance/AuthAction'),
        meta: {
            public: true,
        }
    },
    {
        path: '/select-profile',
        name: 'select-profile',
        component: () => import('@/views/entrance/SelectProfile'),
        meta: {}
    },
    {
        path: '/basic-info',
        name: 'basic-info',
        component: () => import('@/views/entrance/BasicInfo'),
        meta: {}
    },
    {
        path: '/coach/company-invitation',
        name: 'coach-company-invitation',
        component: () => import('@/views/entrance/CoachCompanyInvitation'),
        meta: {}
    },
    {
        path: '/admin',
        name: 'admin.home',
        component: () => import('@/views/admin/Home'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/excel',
        name: 'admin.excel',
        component: () => import('@/views/admin/UploadExcel'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/surveys',
        name: 'admin.surveys',
        component: () => import('@/views/admin/Surveys'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/attach-users',
        name: 'admin.attach-users',
        component: () => import('@/views/admin/AttachUsers'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/confirm-email',
        name: 'admin.confirm-email',
        component: () => import('@/views/admin/ConfirmEmail'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/delete-user',
        name: 'admin.delete-user',
        component: () => import('@/views/admin/DeleteUser'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/restore-user',
        name: 'admin.restore-user',
        component: () => import('@/views/admin/RestoreUser'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/user-access',
        name: 'admin.user-access',
        component: () => import('@/views/admin/UserAccess'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/user-detail',
        name: 'admin.user-detail',
        component: () => import('@/views/admin/UserDetail'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/company-to-coach',
        name: 'admin.company-to-coach',
        component: () => import('@/views/admin/CompanyToCoach'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/invitations',
        name: 'admin.invitations',
        component: () => import('@/views/admin/Invitations'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/errors',
        name: 'admin.errors',
        component: () => import('@/views/admin/Errors'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/config',
        name: 'admin.config',
        component: () => import('@/views/admin/Config'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '/admin/tutorials',
        name: 'admin.tutorials',
        component: () => import('@/views/admin/Tutorials'),
        meta: {
            toolbar: true,
            adminDrawer: true,
        }
    },
    {
        path: '*',
        redirect: { name: 'splash' },
    },
]

const router = new VueRouter({
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition
        return { x: 0, y: 0 }
    }
})

router.beforeEach(async (to, from, next) => {
    const actionParams = new URLSearchParams(window.location.search)
    if (actionParams.get('mode') && actionParams.get('oobCode') && to.name !== 'auth-action') {
        return next({ name: 'auth-action', replace: true })
    }
    if (store.state.dialogStack.length) {
        store.commit('popDialog')
        return next(false)
    }
    if (to.meta && to.meta.public) {
        if (isIosApp() && to.name === 'register') return next({ name: 'login', replace: true })
        return next()
    } else {
        try {
            const authUser = await waitForAuth()
            if (!store.state.account || !store.state.currentUser) {
                if (authUser) await store.dispatch('getUserAccount')
                else store.commit('setBrandLogo', await Storage.getBrandLogo())
            }
            const user = authUser ? store.state.currentUser : null
            const account = store.state.account
            let accountType = account && account.accountType || ''
            let accountName = account && account.name || ''
            let homeRoute = {name: `${accountType}.home`, replace: true}
            if (accountType === 'coach_company') homeRoute.name = 'coach.home'

            if (!user) {
                const credentials = await Storage.getCredentials()
                if (credentials) {
                    store.commit('setRedirectRoute', to.fullPath)
                    return next({name: 'splash', replace: true})
                }
                return to.name === 'login' ? next() : next({name: 'login', replace: true})
            }
            if (!user.emailVerified) return to.name === 'confirm-mail' ? next() : next({name: 'confirm-mail', replace: true})
            if (!accountType) return to.name === 'select-profile' ? next() : next({name: 'select-profile', replace: true})
            if (accountType === 'coach_company') {
                let routes = ['coach.payment.edit', 'coach.select-plan', 'coach.credit-card']
                if (routes.includes(to.name)) return next(false)
                if (account.firstLogin === true) return to.name === 'coach-company-invitation' ? next() : next({name: 'coach-company-invitation', replace: true})
                if (store.state.coachCompanyBlocked) return to.name === 'coach.company-no-plan' ? next() : next({name: 'coach.company-no-plan', replace: true})
                if (to.name === 'coach.company-no-plan') return next(homeRoute)
            }
            if (!accountName) {
                if (accountType === 'coach_company') return next()
                return to.name === 'basic-info' || to.name === 'select-profile' ? next() : next({name: 'basic-info', replace: true})
            }

            if (accountType === 'coach' || accountType === 'company') {
                const hasStripeAccount = account && account.stripeAccount || null
                const hasPaymentMethod = hasStripeAccount && hasStripeAccount.paymentMethod && hasStripeAccount.paymentMethod.id || null
                const hasCurrentSubscription = hasStripeAccount && hasStripeAccount.currentSubscription && hasStripeAccount.currentSubscription.id || null
                const isFreeSubscription = hasStripeAccount && hasStripeAccount.currentSubscription && hasStripeAccount.currentSubscription.isFree === true
                if (!paymentsEnabled() && PAYMENT_ROUTES.includes(to.name)) return next(homeRoute)
                if (!skipsBilling(account) && paymentsEnabled()) {
                    if (!hasPaymentMethod && !isFreeSubscription) return to.name === `${accountType}.credit-card` ? next() : next({name: `${accountType}.credit-card`, replace: true})
                    if (!hasCurrentSubscription) return to.name === `${accountType}.select-plan` ? next() : next({name: `${accountType}.select-plan`, replace: true})
                }
                if (to.name === 'coach.statistics' && isFreeSubscription) return next(false)
            }

            if (accountType === 'student') {
                const hasPausedAccount = account && account.studentSubscription && account.studentSubscription.paused === true
                if (hasPausedAccount && to.name !== 'student.home') return next(false)

                const currentCoachId = account && account.studentSubscription && account.studentSubscription.coachId || null
                if (currentCoachId && !store.state.coachMissing) {
                    if (to.name === 'student.coaches') return next(homeRoute)
                    if (to.name === 'student.coach-profile' && to.params.uid !== currentCoachId) return next(homeRoute)
                }
            }

            if ((accountType && to.name === 'select-profile') || (accountName && to.name === 'basic-info')) return next(homeRoute)
            let noRestrictionRoutes = ['splash', 'login', 'register', 'confirm-mail', 'select-profile', 'basic-info', 'coach-company-invitation', 'admin.home', 'admin.excel', 'admin.surveys', 'admin.attach-users', 'admin.confirm-email', 'admin.delete-user', 'admin.restore-user', 'admin.user-access', 'admin.user-detail', 'admin.company-to-coach', 'admin.invitations', 'admin.errors', 'admin.config', 'admin.tutorials']
            if (accountType === 'coach_company') accountType = 'coach'
            if (!noRestrictionRoutes.includes(to.name) && !to.name.startsWith(accountType)) return next(false)

            return next()
        } catch (error) {
            console.error('router guard error:', error)
            return to.name === 'login' ? next() : next({name: 'login', replace: true})
        }
    }
})

export default router
